<template>
  <div class="stats-panel">
    <Loading v-if="isLoading" class="loading"></Loading>
    <slot name="empty" v-else-if="shouldDisplayEmpty"></slot>
    <template v-else>
      <div class="filters flex flex-column flex-row-m justify-between" v-if="!isWaitingPopulation">
        <div class="w-100 w-50-m">
          <div class="periodselectcontainer pr5-m" v-if="developerStats.length > 1">
            <h6 class="mb2">{{ $t('Select a title') }}</h6>
            <UISelect class="periodselect mb4" v-model="titleValue">
              <option
                v-for="(stat, i) in developerStats"
                :value="stat.contentOwnership.id"
                :key="i"
              >{{ $t(stat.game.title) }}</option>
            </UISelect>
          </div>
          <div v-else>
            <h2 class="f2 mt4 mb3">{{ $t(singleTitle) }}</h2>
          </div>
          <i18n
            class="createdat f7 mb4"
            tag="p"
            path="This title is available on SpringboardVR since: {date}."
            v-if="createdAt"
          >
            <span slot="date">{{ createdAt }}</span>
          </i18n>
        </div>
        <div class="w-100 flex flex-column flex-row-m justify-end pl5-m">
          <div class="flex flex-column mb4 mr3-m">
            <h6 class="mb2">{{ $t('Select a timeframe')}}</h6>
            <div class="flex">
              <UIButton
                class="periodradio f4 flex-auto w-50 mb4"
                :ghost="true"
                name="period"
                :class="{active: periodType === 'month'}"
                @click="periodType = 'month'"
              >{{ $t('Month') }}</UIButton>
              <UIButton
                class="periodradio f4 flex-auto w-50 mb4"
                :ghost="true"
                name="period"
                :class="{active: periodType === 'year'}"
                @click="periodType = 'year'"
              >{{ $t('Year') }}</UIButton>
            </div>
          </div>
          <div class="periodselectcontainer mb4 flex flex-column">
            <h6 class="mb2">{{ periodType === 'month' ? $t('Select a month') : $t('Select a year')}}</h6>
            <UISelect class="periodselect" v-model="periodValue">
              <option v-for="({ key, value }) in filteredPeriodOptions" :value="key" :key="key">{{ value }}</option>
            </UISelect>
          </div>
        </div>
      </div>
      <ContentBlock class="subheader"></ContentBlock>
      <Loading v-if="isLoadingContentStats || this.titleValue === null" class="loading"></Loading>
      <div class="wrapper" v-else-if="periodType">
        <NewGameStats :type="periodType" :stat="contentOwnershipStat" :is-current-date="isCurrentDate"></NewGameStats>
      </div>
    </template>
  </div>
</template>

<script>
import NewGameStats from './StatsPanel/NewGameStats';
import Stats from './StatsPanel';
import Component from 'vue-class-component';
import moment from 'moment';

@Component({
  components: {
    NewGameStats,
  },
})
export default class NewStats extends Stats {
  titleValue = null
  isFetchingContentStats = false

  get contentOwnershipStat () { return this.$store.state.stats.contentOwnershipStat; }

  get filteredPeriodOptions () {
    if (this.periodOptions && Object.keys(this.contentOwnershipStat).length > 1) {
      const pattern = this.periodType === 'month' ? new RegExp('^\\d{4}-\\d{2}') : new RegExp('^\\d{4}');
      const match = this.contentOwnershipStat.contentOwnership.createdAt.match(pattern)[0];
      return this.periodOptions.slice(0).filter(date => {
        return date.key >= match;
      });
    }
  }

  get singleTitle () {
    if (this.developerStats.length === 1) {
      return this.developerStats[0].game.title;
    }
  }

  get createdAt () {
    if (Object.keys(this.contentOwnershipStat).length === 0) return;
    return moment(this.contentOwnershipStat.contentOwnership.createdAt).format('LL');
  }

  get isLoadingContentStats () {
    return this.isFetchingContentStats;
  }

  async created () {
    this.$store.commit('setDeveloperStats', []);
    await this.populateFilterOptions();
    this.$watch(() => this.periodType, this.setDefaultValueInSelect, { immediate: true });
    this.$watch(() => this.periodValue, this.fetchContentOwnershipStat, { immediate: true });
    this.$watch(() => this.titleValue, this.fetchContentOwnershipStat, { immediate: true });
    this.$watch(() => this.developerStats, this.setTitleValue, { immediate: true });
  }

  setTitleValue () {
    if (this.developerStats.length > 0 && this.titleValue === null) this.titleValue = this.developerStats[0].contentOwnership.id;
  }

  async fetchContentOwnershipStat () {
    if (!this.titleValue) return;
    this.isFetchingContentStats = true;
    await this.$store.dispatch('contentOwnershipStat', { type: this.periodType, value: this.periodValue, contentOwnership: { id: this.titleValue } });
    this.isFetchingContentStats = false;
  }
}

</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.stats-panel {
  & >>> .instructions {
    @media (--desktop) {
      /*  flex: 0 0 20rem; */
      margin-right: var(--spacingSm);
    }
  }
  & .subheader >>> .content {
    background: none;
    padding: 0;
  }
  & .subheader >>> .panel {
    background: none;
    box-shadow: none;
  }
  & h6 {
    @apply --f7;

    font-style: normal;
    color: var(--colorStorm);
  }
  & select {
    margin: 0;
  }
  & .lastupdate {
    @apply --f8;
    margin: 0;
  }
  & .lastupdatecontainer,
  & .periodswitch {
    align-self: end;
  }
  & .periodradio:first-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  & .periodradio:last-child {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  & .filters {
    & .periodselectcontainer p {
      color: var(--colorManatee);
    }
  }
  & .createdat {
    font-style: normal;
  }
}
</style>
